export const useLinearCount = (from: number, to: number, interval = 1000, {
  autoStart = false,
  loop = false,
  onFinish = () => {},
  onTick = (arg: number) => {}
} = {}) => {
  const count = ref(from)
  let tid

  const start = () => {
    tid = setInterval(() => {
      from > to ? count.value-- : count.value++
      onTick(count.value)

      if (count.value === to) {
        clearInterval(tid)
        tid = null
        onFinish()

        if (loop) {
          reset()
          start()
        }
      }
    }, interval)
  }

  const stop = () => {
    clearInterval(tid)
    tid = null
  }

  const reset = () => {
    count.value = from
  }

  onUnmounted(() => {
    stop()
  })

  const isFinished = computed(() => count.value === to)

  if (autoStart) start()

  return { count, start, stop, isFinished, reset }
}

interface PreloadOptions {
  type?: 'image' | 'style' | 'script' | 'font'
  as?: string
  crossOrigin?: 'anonymous' | 'use-credentials'
  media?: string
}

export function usePreload(
  url: string,
  options: PreloadOptions = {},
) {
  const head = document.head
  if (head.querySelector(`link[rel="preload"][href="${url}"]`)) {
    return
  }

  const link = document.createElement('link')
  link.rel = 'preload'
  link.href = url

  if (options.as) {
    link.setAttribute('as', options.as)
  }

  if (options.crossOrigin) {
    link.crossOrigin = options.crossOrigin
  }

  if (options.media) {
    link.media = options.media
  }

  head.appendChild(link)
}

export interface PrefetchOptions {
  type?: 'image' | 'style' | 'script' | 'font'
  as?: string
  crossOrigin?: 'anonymous' | 'use-credentials'
  media?: string
}

export function usePrefetch(
  url: string,
  options: PrefetchOptions = {},
) {
  const head = document.head
  if (head.querySelector(`link[rel="prefetch"][href="${url}"]`)) {
    return
  }

  const link = document.createElement('link')
  link.rel = 'prefetch'
  link.href = url

  if (options.as) {
    link.setAttribute('as', options.as)
  }

  if (options.crossOrigin) {
    link.crossOrigin = options.crossOrigin
  }

  if (options.media) {
    link.media = options.media
  }

  head.appendChild(link)
}
